import React, {useState, useCallback} from 'react';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  Chip,
  Collapse,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderOffIcon from '@mui/icons-material/FolderOff';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import {useQuery} from '@apollo/client';
import {GET_SOURCE_ITEM_TREE} from '../../graphql/queries';
import {colors} from '../../theme/colors';

interface TreeNode {
  id: string;
  kind: string;
  label: string;
  url?: string;
  title?: string;
  count: number;
  hasChildren: boolean;
}

interface WebExplorerTreeProps {
  orgId: string;
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string | null) => void;
}

function nodeIcon(kind: string) {
  switch (kind) {
    case 'domain': return <LanguageIcon sx={{fontSize: 18, color: colors.blue[500]}} />;
    case 'prefix': return <FolderIcon sx={{fontSize: 18, color: '#F59E0B'}} />;
    case 'page': return <DescriptionIcon sx={{fontSize: 18, color: colors.gray[500]}} />;
    case 'uncategorized': return <FolderOffIcon sx={{fontSize: 18, color: colors.gray[400]}} />;
    default: return <FolderIcon sx={{fontSize: 18}} />;
  }
}

function humanizeSegment(segment: string): string {
  try {
    segment = decodeURIComponent(segment);
  } catch { /* ignore */ }
  return segment
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

function getNodeLabels(node: TreeNode): { primary: string; secondary?: string; tooltip: string } {
  switch (node.kind) {
    case 'domain':
      return { primary: node.label, tooltip: node.label };
    case 'prefix': {
      const segments = node.label.split('/').filter(Boolean);
      const lastSegment = segments[segments.length - 1];
      const primary = lastSegment ? humanizeSegment(lastSegment) : node.label;
      return { primary, secondary: node.label, tooltip: node.label };
    }
    case 'page': {
      const primary = node.title || (() => {
        try {
          const parsed = new URL(node.url || '');
          const segments = parsed.pathname.split('/').filter(Boolean);
          const last = segments[segments.length - 1];
          return last ? humanizeSegment(last) : node.url || node.label;
        } catch {
          return node.label;
        }
      })();
      let secondary: string | undefined;
      try {
        const parsed = new URL(node.url || '');
        secondary = parsed.pathname + (parsed.search || '');
      } catch {
        secondary = node.url || node.label;
      }
      return { primary, secondary, tooltip: node.url || node.label };
    }
    case 'uncategorized':
      return { primary: 'Uncategorized', tooltip: 'Uncategorized' };
    default:
      return { primary: node.label, tooltip: node.label };
  }
}

function TreeNodeItem({
  node,
  orgId,
  selectedNodeId,
  onSelectNode,
  depth,
}: {
  node: TreeNode;
  orgId: string;
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string | null) => void;
  depth: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const isSelected = selectedNodeId === node.id;

  const {data, loading} = useQuery(GET_SOURCE_ITEM_TREE, {
    variables: {orgId, parentId: node.id},
    skip: !expanded || !node.hasChildren,
  });

  const children: TreeNode[] = data?.sourceItemTree ?? [];
  const {primary, secondary, tooltip} = getNodeLabels(node);

  const handleToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (node.hasChildren) {
      setExpanded(prev => !prev);
    }
  }, [node.hasChildren]);

  const handleSelect = useCallback(() => {
    onSelectNode(isSelected ? null : node.id);
  }, [onSelectNode, node.id, isSelected]);

  return (
    <>
      <Tooltip title={tooltip} placement="right" enterDelay={500}>
        <ListItemButton
          onClick={handleSelect}
          selected={isSelected}
          sx={{
            pl: 1.5 + depth * 2,
            py: 0.5,
            minHeight: 36,
            '&.Mui-selected': {bgcolor: colors.blue[50], '&:hover': {bgcolor: colors.blue[100]}},
          }}
        >
          <Box
            onClick={handleToggle}
            sx={{
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 0.5,
              cursor: node.hasChildren ? 'pointer' : 'default',
            }}
          >
            {node.hasChildren ? (
              expanded ? <ExpandMoreIcon sx={{fontSize: 16, color: '#6B7280'}} /> : <ChevronRightIcon sx={{fontSize: 16, color: '#6B7280'}} />
            ) : null}
          </Box>
          <ListItemIcon sx={{minWidth: 28}}>
            {nodeIcon(node.kind)}
          </ListItemIcon>
          <Box sx={{flex: 1, minWidth: 0, mr: 0.5}}>
            <Typography sx={{fontSize: 13, fontWeight: isSelected ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
              {primary}
            </Typography>
            {secondary && (
              <Typography sx={{fontSize: 11, color: '#9CA3AF', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                {secondary}
              </Typography>
            )}
          </Box>
          <Chip
            label={node.count}
            size="small"
            variant="outlined"
            sx={{height: 18, fontSize: 11, fontWeight: 500, ml: 0.5, flexShrink: 0, '& .MuiChip-label': {px: 0.75}}}
          />
        </ListItemButton>
      </Tooltip>
      {node.hasChildren && (
        <Collapse in={expanded}>
          {loading ? (
            <Box sx={{pl: 4 + depth * 2, py: 1}}>
              <CircularProgress size={14} />
            </Box>
          ) : (
            children.map(child => (
              <TreeNodeItem
                key={child.id}
                node={child}
                orgId={orgId}
                selectedNodeId={selectedNodeId}
                onSelectNode={onSelectNode}
                depth={depth + 1}
              />
            ))
          )}
        </Collapse>
      )}
    </>
  );
}

export default function WebExplorerTree({orgId, selectedNodeId, onSelectNode}: WebExplorerTreeProps) {
  const {data, loading} = useQuery(GET_SOURCE_ITEM_TREE, {
    variables: {orgId, parentId: null},
  });

  const nodes: TreeNode[] = data?.sourceItemTree ?? [];

  return (
    <Box sx={{height: '100%', display: 'flex', flexDirection: 'column'}}>
      <Box sx={{px: 2, py: 1.5, borderBottom: '1px solid #E5E7EB'}}>
        <Typography sx={{fontSize: 13, fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
          Sources
        </Typography>
      </Box>
      <Box sx={{flex: 1, overflow: 'auto'}}>
        <List dense disablePadding>
          {/* "All Sources" root option */}
          <ListItemButton
            selected={selectedNodeId === null}
            onClick={() => onSelectNode(null)}
            sx={{
              pl: 1.5,
              py: 0.5,
              minHeight: 36,
              '&.Mui-selected': {bgcolor: colors.blue[50], '&:hover': {bgcolor: colors.blue[100]}},
            }}
          >
            <Box sx={{width: 20, mr: 0.5}} />
            <ListItemIcon sx={{minWidth: 28}}>
              <LibraryBooksIcon sx={{fontSize: 18, color: colors.blue[600]}} />
            </ListItemIcon>
            <Box sx={{flex: 1, minWidth: 0}}>
              <Typography sx={{fontSize: 13, fontWeight: selectedNodeId === null ? 600 : 400}}>
                All Sources
              </Typography>
            </Box>
          </ListItemButton>

          {loading ? (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 3}}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            nodes.map(node => (
              <TreeNodeItem
                key={node.id}
                node={node}
                orgId={orgId}
                selectedNodeId={selectedNodeId}
                onSelectNode={onSelectNode}
                depth={0}
              />
            ))
          )}
        </List>
      </Box>
    </Box>
  );
}
